import React, { useState } from 'react';
import './SortableTable.css';

/**
 * SortableTable component that allows sorting by column headers
 * 
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of objects to display in the table
 * @param {Array} props.columns - Array of column definitions with { id, label, accessor, sortAccessor, sortable }
 * @param {Function} props.onRowHover - Function called when hovering over a row
 * @param {Function} props.onRowLeave - Function called when leaving a row
 * @param {Function} props.renderRow - Custom row renderer function (optional)
 * @param {JSX.Element} props.summaryRow - Summary row to display at the bottom (optional)
 * @returns {JSX.Element} SortableTable component
 */
const SortableTable = ({ 
  data, 
  columns, 
  onRowHover, 
  onRowLeave, 
  renderRow,
  summaryRow
}) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  // Handle sort request
  const requestSort = (key) => {
    let direction = 'asc';
    
    // If already sorting by this key, toggle direction
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = () => {
    if (!sortConfig.key) {
      return data;
    }

    return [...data].sort((a, b) => {
      // Find the column definition for the sort key
      const column = columns.find(col => col.id === sortConfig.key);
      if (!column) return 0;

      // Get values using the sortAccessor (if provided) or accessor function or property
      let aValue;
      let bValue;
      
      if (column.sortAccessor) {
        // Use sortAccessor if provided
        aValue = typeof column.sortAccessor === 'function' 
          ? column.sortAccessor(a) 
          : a[column.sortAccessor];
        
        bValue = typeof column.sortAccessor === 'function' 
          ? column.sortAccessor(b) 
          : b[column.sortAccessor];
      } else {
        // Fall back to accessor
        aValue = typeof column.accessor === 'function' 
          ? column.accessor(a) 
          : a[column.accessor];
        
        bValue = typeof column.accessor === 'function' 
          ? column.accessor(b) 
          : b[column.accessor];
      }

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      // Handle numeric comparison for area
      if (column.id === 'area' && typeof aValue === 'string') {
        // Extract numeric value from formatted area string (e.g., "123.45 ft²")
        aValue = parseFloat(aValue.replace(/[^0-9.]/g, ''));
        bValue = parseFloat(bValue.replace(/[^0-9.]/g, ''));
      }

      // Compare values
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (columnId) => {
    if (sortConfig.key !== columnId) {
      return <span className="sort-indicator">↕</span>;
    }
    
    return sortConfig.direction === 'asc' 
      ? <span className="sort-indicator asc">↑</span> 
      : <span className="sort-indicator desc">↓</span>;
  };

  // Get sorted data
  const sortedData = getSortedData();

  return (
    <table className="sortable-table">
      <thead>
        <tr>
          {columns.map(column => (
            <th 
              key={column.id}
              onClick={() => column.sortable && requestSort(column.id)}
              className={`${column.sortable ? 'sortable' : ''} ${sortConfig.key === column.id ? 'active-sort' : ''}`}
            >
              {column.label}
              {column.sortable && getSortDirectionIndicator(column.id)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedData.map((item, index) => (
          renderRow ? (
            renderRow(item, index, onRowHover, onRowLeave)
          ) : (
            <tr 
              key={index}
              onMouseEnter={() => onRowHover && onRowHover(item)}
              onMouseLeave={() => onRowLeave && onRowLeave()}
            >
              {columns.map(column => (
                <td key={column.id}>
                  {typeof column.accessor === 'function' 
                    ? column.accessor(item) 
                    : item[column.accessor]}
                </td>
              ))}
            </tr>
          )
        ))}
        {summaryRow && summaryRow}
      </tbody>
    </table>
  );
};

export default SortableTable; 